export type Inline =
  | { kind: "text"; text: string }
  | { kind: "strong"; children: Inline[] }
  | { kind: "em"; children: Inline[] }
  | { kind: "del"; children: Inline[] }
  | { kind: "code"; text: string }
  | { kind: "link"; href: string; children: Inline[] }
  | { kind: "break" };

export type Align = "left" | "right" | "center" | null;

export type Block =
  | { kind: "paragraph"; children: Inline[] }
  | { kind: "heading"; level: 1 | 2 | 3 | 4 | 5 | 6; children: Inline[] }
  | { kind: "code"; lang: string | null; text: string }
  | { kind: "list"; ordered: boolean; start: number; items: Block[][] }
  | { kind: "table"; align: Align[]; head: Inline[][]; rows: Inline[][][] }
  | { kind: "rule" };

const SAFE_SCHEMES = ["http:", "https:", "mailto:"] as const;

const URL_NOISE =
  // biome-ignore lint/suspicious/noControlCharactersInRegex: the URL sanitizer deliberately removes control and invisible characters before scheme validation.
  /[\u0000-\u0020\u007f-\u00a0\u1680\u180e\u2000-\u200f\u2028-\u202f\u205f-\u206f\u3000\ufeff]/g;

function safeHref(raw: string): string | null {
  const url = raw.replace(URL_NOISE, "");
  if (url === "") return null;
  if (url.startsWith("//")) return null;
  const colon = url.indexOf(":");
  if (colon < 0) return null;
  for (const ch of ["/", "?", "#"]) {
    const at = url.indexOf(ch);
    if (at >= 0 && at < colon) return null;
  }
  const scheme = url.slice(0, colon + 1).toLowerCase();
  return (SAFE_SCHEMES as readonly string[]).includes(scheme) ? url : null;
}

const ESCAPABLE = "\\`*_{}[]()#+-.!|~>";

function pushText(out: Inline[], text: string): void {
  if (text === "") return;
  const last = out[out.length - 1];
  if (last && last.kind === "text") last.text += text;
  else out.push({ kind: "text", text });
}

function findDelim(src: string, needle: string, from: number): number {
  for (let i = from; i + needle.length <= src.length; i++) {
    if (src[i] === "\\") {
      i++;
      continue;
    }
    if (src.startsWith(needle, i)) return i;
  }
  return -1;
}

const EMPHASIS = [
  ["***", "both"],
  ["**", "strong"],
  ["~~", "del"],
  ["*", "em"],
] as const;

function readEmphasis(src: string, at: number): { node: Inline; next: number } | null {
  for (const [delim, kind] of EMPHASIS) {
    if (!src.startsWith(delim, at)) continue;
    const from = at + delim.length;
    if (from >= src.length || /\s/.test(src[from])) continue;
    const close = findDelim(src, delim, from);
    if (close < 0 || close === from) continue;
    if (/\s/.test(src[close - 1])) continue;
    const inner = parseInline(src.slice(from, close));
    const node: Inline =
      kind === "both"
        ? { kind: "strong", children: [{ kind: "em", children: inner }] }
        : { kind, children: inner };
    return { node, next: close + delim.length };
  }
  return null;
}

function readLink(src: string, at: number): { node: Inline; next: number } | null {
  let depth = 0;
  let close = -1;
  for (let i = at; i < src.length; i++) {
    if (src[i] === "\\") {
      i++;
      continue;
    }
    if (src[i] === "[") depth++;
    else if (src[i] === "]") {
      depth--;
      if (depth === 0) {
        close = i;
        break;
      }
    }
  }
  if (close < 0 || src[close + 1] !== "(") return null;
  let paren = 0;
  let end = -1;
  for (let i = close + 1; i < src.length; i++) {
    if (src[i] === "\\") {
      i++;
      continue;
    }
    if (src[i] === "(") paren++;
    else if (src[i] === ")") {
      paren--;
      if (paren === 0) {
        end = i;
        break;
      }
    }
  }
  if (end < 0) return null;
  const dest = src
    .slice(close + 2, end)
    .trim()
    .replace(/^<|>$/g, "");
  const href = safeHref(dest);
  if (href === null) return null;
  return {
    node: { kind: "link", href, children: parseInline(src.slice(at + 1, close)) },
    next: end + 1,
  };
}

function parseInline(src: string): Inline[] {
  const out: Inline[] = [];
  let buf = "";
  let i = 0;
  const flush = () => {
    pushText(out, buf);
    buf = "";
  };

  while (i < src.length) {
    const c = src[i];

    if (c === "\\" && i + 1 < src.length && ESCAPABLE.includes(src[i + 1])) {
      buf += src[i + 1];
      i += 2;
      continue;
    }

    if (c === "\n") {
      flush();
      out.push({ kind: "break" });
      i++;
      continue;
    }

    if (c === "`") {
      let n = 0;
      while (src[i + n] === "`") n++;
      const fence = "`".repeat(n);
      let close = src.indexOf(fence, i + n);
      while (close >= 0 && src[close + n] === "`") close = src.indexOf(fence, close + n + 1);
      if (close >= 0) {
        let text = src.slice(i + n, close);
        if (text.length > 1 && text.startsWith(" ") && text.endsWith(" ") && text.trim() !== "") {
          text = text.slice(1, -1);
        }
        flush();
        out.push({ kind: "code", text: text.replace(/\n/g, " ") });
        i = close + n;
        continue;
      }
      buf += fence;
      i += n;
      continue;
    }

    if (c === "[") {
      const bang = src[i - 1] === "!" && src[i - 2] !== "\\";
      const link = bang ? null : readLink(src, i);
      if (link) {
        flush();
        out.push(link.node);
        i = link.next;
        continue;
      }
      buf += c;
      i++;
      continue;
    }

    const emph = readEmphasis(src, i);
    if (emph) {
      flush();
      out.push(emph.node);
      i = emph.next;
      continue;
    }

    buf += c;
    i++;
  }

  flush();
  return out;
}

const ATX = /^ {0,3}(#{1,6})(?:[ \t]+(.*?))?[ \t]*$/;
const FENCE = /^ {0,3}(`{3,}|~{3,})[ \t]*([^`\s]*)[ \t]*$/;
const RULE = /^ {0,3}([-*_])[ \t]*(?:\1[ \t]*){2,}$/;
const ITEM = /^( *)([-*+]|\d{1,9}[.)])([ \t]+)(.*)$/;
const DELIM_ROW = /^ {0,3}\|?[ \t]*:?-+:?[ \t]*(\|[ \t]*:?-+:?[ \t]*)*\|?[ \t]*$/;

function isDelimiterRow(line: string | undefined): boolean {
  return Boolean(line?.includes("-") && DELIM_ROW.test(line));
}

function splitRow(line: string): string[] {
  const cells: string[] = [];
  let cur = "";
  for (let i = 0; i < line.length; i++) {
    if (line[i] === "\\" && line[i + 1] === "|") {
      cur += "|";
      i++;
      continue;
    }
    if (line[i] === "|") {
      cells.push(cur);
      cur = "";
      continue;
    }
    cur += line[i];
  }
  cells.push(cur);
  if (cells.length && cells[0].trim() === "") cells.shift();
  if (cells.length && cells[cells.length - 1].trim() === "") cells.pop();
  return cells.map((c) => c.trim());
}

function alignOf(cell: string): Align {
  const c = cell.trim();
  const left = c.startsWith(":");
  const right = c.endsWith(":");
  if (left && right) return "center";
  if (right) return "right";
  if (left) return "left";
  return null;
}

function startsBlock(line: string, next: string | undefined): boolean {
  if (line.trim() === "") return true;
  if (ATX.test(line) || FENCE.test(line) || RULE.test(line) || ITEM.test(line)) return true;
  return line.includes("|") && isDelimiterRow(next);
}

export function parseMarkdown(src: string): Block[] {
  return parseBlocks(src.replace(/\r\n?/g, "\n").split("\n"));
}

function parseBlocks(lines: string[]): Block[] {
  const out: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === "") {
      i++;
      continue;
    }

    const fence = FENCE.exec(line);
    if (fence) {
      const char = fence[1][0];
      const closer = new RegExp(`^ {0,3}\\${char}{${fence[1].length},}[ \\t]*$`);
      const body: string[] = [];
      i++;
      while (i < lines.length && !closer.test(lines[i])) {
        body.push(lines[i]);
        i++;
      }
      if (i < lines.length) i++;
      out.push({ kind: "code", lang: fence[2] || null, text: body.join("\n") });
      continue;
    }

    const atx = ATX.exec(line);
    if (atx) {
      out.push({
        kind: "heading",
        level: atx[1].length as 1 | 2 | 3 | 4 | 5 | 6,
        children: parseInline((atx[2] ?? "").replace(/[ \t]+#+[ \t]*$/, "")),
      });
      i++;
      continue;
    }

    if (RULE.test(line)) {
      out.push({ kind: "rule" });
      i++;
      continue;
    }

    if (line.includes("|") && isDelimiterRow(lines[i + 1])) {
      const head = splitRow(line);
      const align = splitRow(lines[i + 1]).map(alignOf);
      i += 2;
      const rows: Inline[][][] = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim() !== "") {
        const cells = splitRow(lines[i]);
        while (cells.length < head.length) cells.push("");
        rows.push(cells.slice(0, head.length).map(parseInline));
        i++;
      }
      out.push({ kind: "table", align, head: head.map(parseInline), rows });
      continue;
    }

    if (ITEM.test(line)) {
      const [list, next] = parseList(lines, i);
      out.push(list);
      i = next;
      continue;
    }

    const para: string[] = [];
    while (i < lines.length && !startsBlock(lines[i], lines[i + 1])) {
      para.push(lines[i]);
      i++;
    }
    if (para.length === 0) {
      para.push(lines[i]);
      i++;
    }
    out.push({ kind: "paragraph", children: parseInline(para.join("\n")) });
  }

  return out;
}

function parseList(lines: string[], start: number): [Block, number] {
  const first = ITEM.exec(lines[start]);
  if (!first) return [{ kind: "list", ordered: false, start: 1, items: [] }, start + 1];
  const baseIndent = first[1].length;
  const ordered = /\d/.test(first[2]);
  const startNo = ordered ? parseInt(first[2], 10) : 1;
  const items: Block[][] = [];
  let i = start;

  while (i < lines.length) {
    const m = ITEM.exec(lines[i]);
    if (!m) break;
    const indent = m[1].length;
    if (indent < baseIndent || indent > baseIndent + 3) break;
    if (/\d/.test(m[2]) !== ordered) break;

    const contentIndent = indent + m[2].length + m[3].length;
    const body = [m[4]];
    i++;
    while (i < lines.length) {
      const l = lines[i];
      if (l.trim() === "") {
        const after = lines[i + 1];
        if (after === undefined || after.search(/\S/) < contentIndent) break;
        body.push("");
        i++;
        continue;
      }
      if (l.search(/\S/) >= contentIndent) {
        body.push(l.slice(contentIndent));
        i++;
        continue;
      }
      if (!ITEM.test(l) && !startsBlock(l, lines[i + 1])) {
        body.push(l.trim());
        i++;
        continue;
      }
      break;
    }
    items.push(parseBlocks(body));
  }

  return [{ kind: "list", ordered, start: startNo, items }, i];
}
